import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Alert,
  Box,
  Chip,
  Autocomplete
} from '@mui/material';
import { toast } from 'react-hot-toast';

// Types
interface Account {
  id: number;
  code: string;
  name: string;
  account_type: string;
  parent_id?: number;
  vat_rate: string;
  is_vat_applicable: boolean;
  is_active: boolean;
  current_balance: number;
  full_name: string;
}

interface EditAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onAccountUpdated: () => void;
  account: Account | null;
  accounts: Account[];
}

interface AccountUpdateData {
  name: string;
  account_type: string;
  parent_id: number | null;
  vat_rate: string;
  is_vat_applicable: boolean;
  is_active: boolean;
}

const EditAccountDialog: React.FC<EditAccountDialogProps> = ({
  open,
  onClose,
  onAccountUpdated,
  account,
  accounts
}) => {
  const [formData, setFormData] = useState<AccountUpdateData>({
    name: '',
    account_type: '',
    parent_id: null,
    vat_rate: 'standard',
    is_vat_applicable: true,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form when account changes
  useEffect(() => {
    if (account && open) {
      setFormData({
        name: account.name,
        account_type: account.account_type,
        parent_id: account.parent_id || null,
        vat_rate: account.vat_rate,
        is_vat_applicable: account.is_vat_applicable,
        is_active: account.is_active
      });
      setErrors({});
    }
  }, [account, open]);

  // Account type options
  const accountTypes = [
    { value: 'asset', label: 'Asset', description: 'Resources owned by the business' },
    { value: 'liability', label: 'Liability', description: 'Debts owed by the business' },
    { value: 'equity', label: 'Equity', description: 'Owner\'s stake in the business' },
    { value: 'income', label: 'Income', description: 'Revenue and other income' },
    { value: 'expense', label: 'Expense', description: 'Costs and expenses' }
  ];

  // VAT rate options
  const vatRates = [
    { value: 'standard', label: 'Standard (20%)', percentage: 20 },
    { value: 'reduced', label: 'Reduced (5%)', percentage: 5 },
    { value: 'zero', label: 'Zero (0%)', percentage: 0 },
    { value: 'exempt', label: 'Exempt', percentage: 0 }
  ];

  // Handle form field changes
  const handleChange = (field: keyof AccountUpdateData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-configure VAT settings based on account type
    if (field === 'account_type') {
      if (value === 'asset' || value === 'liability' || value === 'equity') {
        setFormData(prev => ({
          ...prev,
          vat_rate: 'exempt',
          is_vat_applicable: false
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          vat_rate: 'standard',
          is_vat_applicable: true
        }));
      }
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Account name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Account name must be less than 255 characters';
    }

    if (!formData.account_type) {
      newErrors.account_type = 'Account type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!account || !validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/v1/accounts/${account.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      toast.success(`Account "${result.full_name}" updated successfully!`);
      onAccountUpdated();
      onClose();
    } catch (err) {
      console.error('Error updating account:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update account');
    } finally {
      setLoading(false);
    }
  };

  // Handle account deletion/deactivation
  const handleDelete = async () => {
    if (!account) return;

    if (!confirm(`Are you sure you want to ${account.is_active ? 'deactivate' : 'activate'} this account?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/v1/accounts/${account.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      toast.success(result.message);
      onAccountUpdated();
      onClose();
    } catch (err) {
      console.error('Error deleting account:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  // Get parent account options (same type only, excluding self)
  const parentAccountOptions = accounts.filter(acc => 
    acc.account_type === formData.account_type && 
    acc.is_active && 
    acc.id !== account?.id
  );

  if (!account) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Edit Account
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {account.code} - {account.name}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Account Code (Read-only) */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Account Code"
              value={account.code}
              disabled
              helperText="Account code cannot be changed"
            />
          </Grid>

          {/* Current Balance (Read-only) */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Current Balance"
              value={`£${account.current_balance.toLocaleString()}`}
              disabled
              helperText="Current account balance"
            />
          </Grid>

          {/* Account Status */}
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                />
              }
              label={formData.is_active ? "Active" : "Inactive"}
            />
          </Grid>

          {/* Account Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Account Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name || 'Descriptive name for the account'}
            />
          </Grid>

          {/* Account Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.account_type}>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={formData.account_type}
                label="Account Type"
                onChange={(e) => handleChange('account_type', e.target.value)}
              >
                {accountTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box>
                      <Typography variant="body1">{type.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {type.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.account_type && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.account_type}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Parent Account */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={parentAccountOptions}
              getOptionLabel={(option) => option.full_name}
              value={parentAccountOptions.find(acc => acc.id === formData.parent_id) || null}
              onChange={(_, value) => handleChange('parent_id', value?.id || null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Account (Optional)"
                  helperText="Select a parent account for hierarchy"
                />
              )}
            />
          </Grid>

          {/* VAT Configuration */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!formData.is_vat_applicable}>
              <InputLabel>VAT Rate</InputLabel>
              <Select
                value={formData.vat_rate}
                label="VAT Rate"
                onChange={(e) => handleChange('vat_rate', e.target.value)}
              >
                {vatRates.map((rate) => (
                  <MenuItem key={rate.value} value={rate.value}>
                    {rate.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_vat_applicable}
                  onChange={(e) => handleChange('is_vat_applicable', e.target.checked)}
                />
              }
              label="VAT Applicable"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Whether this account is subject to VAT
            </Typography>
          </Grid>

          {/* Warning for balance accounts */}
          {account.current_balance !== 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                This account has a current balance of £{account.current_balance.toLocaleString()}. 
                Changing the account type may affect financial reports.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleDelete} 
          color="error" 
          disabled={loading}
        >
          {account.is_active ? 'Deactivate' : 'Activate'}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Updating...' : 'Update Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAccountDialog;
